/**
 * RegisterAiSystemDialog — Trust bug #11.
 *
 * Real AI-system registration form wired to POST /governance/ai-systems.
 * Replaces the theatrical "Register AI System wizard opened" toast that opened
 * nothing. Built-in agents/models are auto-imported; this flow registers
 * third-party or external AI components into the inventory for ISO 42001 /
 * NIST AI RMF / EU AI Act reporting.
 *
 * The backend requires (systemKind, sourceTable, sourceId<uuid>, displayName,
 * status). For an external component the operator has no internal source row,
 * so we record sourceTable='external' and mint a stable UUID handle for
 * sourceId (or accept one they paste). The remaining risk/oversight fields
 * mirror the EU AI Act high-risk registration model.
 */

import { useState } from 'react';
import { Bot, Plus } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  useRegisterAiSystem,
  type AiSystemKind,
  type RegisterAiSystemInput,
} from '@/lib/use-trust-ai-systems';

type RiskClass = NonNullable<RegisterAiSystemInput['riskClassification']>;
type DecisionAuthority = NonNullable<RegisterAiSystemInput['decisionAuthority']>;
type Oversight = NonNullable<RegisterAiSystemInput['humanOversight']>;
type SystemStatus = NonNullable<RegisterAiSystemInput['status']>;

const KIND_OPTIONS: Array<{ value: AiSystemKind; label: string }> = [
  { value: 'agent', label: 'Agent' },
  { value: 'model', label: 'Model' },
  { value: 'rag_pipeline', label: 'RAG Pipeline' },
  { value: 'embedding_model', label: 'Embedding Model' },
  { value: 'reranker', label: 'Reranker' },
  { value: 'mcp_install', label: 'MCP Server' },
  { value: 'workflow_definition', label: 'Workflow' },
  { value: 'custom_function', label: 'Custom Function' },
];

export function RegisterAiSystemDialog({
  open, onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const register = useRegisterAiSystem();

  const [systemKind, setSystemKind] = useState<AiSystemKind>('agent');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [useCaseSummary, setUseCaseSummary] = useState('');
  const [status, setStatus] = useState<SystemStatus>('in_development');
  const [riskClassification, setRiskClassification] = useState<RiskClass>('limited');
  const [decisionAuthority, setDecisionAuthority] = useState<DecisionAuthority>('ai_recommend');
  const [humanOversight, setHumanOversight] = useState<Oversight>('continuous');
  const [ownerTeam, setOwnerTeam] = useState('');
  const [externalRef, setExternalRef] = useState('');

  const reset = () => {
    setSystemKind('agent');
    setDisplayName('');
    setDescription('');
    setUseCaseSummary('');
    setStatus('in_development');
    setRiskClassification('limited');
    setDecisionAuthority('ai_recommend');
    setHumanOversight('continuous');
    setOwnerTeam('');
    setExternalRef('');
  };

  const canSave = displayName.trim().length >= 2;

  const handleSubmit = () => {
    if (!canSave) {
      toast.error('Display name is required (min 2 characters).');
      return;
    }
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const ref = externalRef.trim();
    // sourceId must be a UUID. Accept a pasted UUID; otherwise mint one so the
    // external component still gets a stable inventory handle.
    const sourceId = UUID_RE.test(ref) ? ref : crypto.randomUUID();

    register.mutate(
      {
        systemKind,
        sourceTable: 'external',
        sourceId,
        displayName: displayName.trim(),
        description: description.trim() || null,
        status,
        riskClassification,
        useCaseSummary: useCaseSummary.trim() || null,
        dataClassifications: [],
        decisionAuthority,
        humanOversight,
        ownerTeam: ownerTeam.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success('AI system registered', {
            description: `${displayName.trim()} is now in the inventory.`,
          });
          reset();
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error('Registration failed', { description: err.message });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            Register AI System
          </DialogTitle>
          <DialogDescription>
            Add a third-party or external AI component to the inventory. Built-in
            agents and models are auto-registered.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs font-semibold">System Type</Label>
              <Select value={systemKind} onValueChange={(v) => setSystemKind(v as AiSystemKind)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ais-name" className="text-xs font-semibold">
                Display Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="ais-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Vendor Claims Triage Model"
                className="h-9 text-xs"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ais-desc" className="text-xs font-semibold">Description</Label>
            <Textarea
              id="ais-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this system and where does it run?"
              rows={2}
              className="text-xs"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ais-usecase" className="text-xs font-semibold">Intended Use / Purpose</Label>
            <Textarea
              id="ais-usecase"
              value={useCaseSummary}
              onChange={(e) => setUseCaseSummary(e.target.value)}
              placeholder="Primary use case, inputs, outputs, and known limitations."
              rows={2}
              className="text-xs"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Lifecycle Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as SystemStatus)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_development" className="text-xs">In Development</SelectItem>
                  <SelectItem value="in_production" className="text-xs">In Production</SelectItem>
                  <SelectItem value="deprecated" className="text-xs">Deprecated</SelectItem>
                  <SelectItem value="retired" className="text-xs">Retired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Risk Tier (EU AI Act)</Label>
              <Select value={riskClassification} onValueChange={(v) => setRiskClassification(v as RiskClass)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="minimal" className="text-xs">Minimal</SelectItem>
                  <SelectItem value="limited" className="text-xs">Limited</SelectItem>
                  <SelectItem value="high" className="text-xs">High</SelectItem>
                  <SelectItem value="unacceptable" className="text-xs">Unacceptable</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Decision Authority</Label>
              <Select value={decisionAuthority} onValueChange={(v) => setDecisionAuthority(v as DecisionAuthority)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="human" className="text-xs">Human Only</SelectItem>
                  <SelectItem value="ai_recommend" className="text-xs">AI Recommends</SelectItem>
                  <SelectItem value="ai_decide_with_review" className="text-xs">AI Decides (with review)</SelectItem>
                  <SelectItem value="ai_decide" className="text-xs">AI Decides (autonomous)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Human Oversight</Label>
              <Select value={humanOversight} onValueChange={(v) => setHumanOversight(v as Oversight)}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="continuous" className="text-xs">Continuous</SelectItem>
                  <SelectItem value="sample" className="text-xs">Sampled</SelectItem>
                  <SelectItem value="on_demand" className="text-xs">On Demand</SelectItem>
                  <SelectItem value="none" className="text-xs">None (autonomous)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="ais-team" className="text-xs font-semibold">Owner Team</Label>
              <Input
                id="ais-team"
                value={ownerTeam}
                onChange={(e) => setOwnerTeam(e.target.value)}
                placeholder="e.g. ML Platform"
                className="h-9 text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ais-ref" className="text-xs font-semibold">
                External Reference <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="ais-ref"
                value={externalRef}
                onChange={(e) => setExternalRef(e.target.value)}
                placeholder="Vendor ID / UUID (auto-generated if blank)"
                className="h-9 text-xs"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={register.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSave || register.isPending}>
            <Plus className="h-4 w-4 mr-1" />
            {register.isPending ? 'Registering…' : 'Register System'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
